import { v4 as uuid } from "uuid";
import type { Recipe, Ingredient, MethodStep } from "../types";
import type { ExtractedRecipe } from "../types/recipe-import";

/**
 * Validation result with errors and warnings
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Raw ingredient data from AI (before validation)
 */
interface RawIngredient {
  name?: unknown;
  amount?: unknown;
  unit?: unknown;
}

/**
 * Raw method step from AI (before validation)
 */
type RawMethodStep = string | { step?: string; text?: string } | unknown;

/**
 * Raw recipe data from AI (before validation)
 */
interface RawRecipeData {
  name?: unknown;
  serves?: unknown;
  ingredients?: unknown;
  method?: unknown;
  labels?: unknown;
  notes?: unknown;
  calories?: unknown;
  caloriesEstimated?: unknown;
}

/**
 * Clean up ingredient amount to extract just the numeric/fraction part
 * Handles various edge cases:
 * - Parenthetical conversions: "30g (2 tbsp)" -> "30"
 * - Unicode fractions: "1½ cups" -> "1½"
 * - Ranges: "1-2 cups" -> "1"
 * - Descriptions: "a pinch of" -> ""
 * - Multiple spaces: "1  1/2" -> "1 1/2"
 */
function cleanIngredientAmount(amount: string): string {
  if (!amount || typeof amount !== "string") return "";

  // If there's a parenthesis, take only the part before it
  let cleaned = amount.split("(")[0].trim();

  // Handle ranges by taking the first number
  if (cleaned.includes("-") || cleaned.includes("to")) {
    const rangeMatch = cleaned.match(/^([\d\s½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞/.]+)/);
    if (rangeMatch) cleaned = rangeMatch[0].trim();
  }

  // Extract just numbers, fractions, and fraction symbols
  // Match patterns like: "2", "1.5", "1/2", "1 1/2", "1½"
  const match = cleaned.match(/^[\d\s½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞/.]+/);

  if (!match) {
    // Check for text descriptions like "a pinch", "to taste", etc.
    const textDescriptions =
      /^(a\s+)?(pinch|dash|handful|to\s+taste|some|few)/i;
    if (textDescriptions.test(cleaned)) {
      return ""; // Return empty for descriptive amounts
    }
    return cleaned;
  }

  // Normalize multiple spaces
  return match[0].trim().replace(/\s+/g, " ");
}

/**
 * Extract unit from amount if it's combined
 * Examples:
 * - "30g" -> { amount: "30", unit: "g" }
 * - "125ml" -> { amount: "125", unit: "ml" }
 * - "2 1/2 cups" -> { amount: "2 1/2", unit: "cups" }
 */
function parseAmountAndUnit(
  amountStr: string,
  unitStr: string
): { amount: string; unit: string } {
  const cleaned = cleanIngredientAmount(amountStr);

  // If unit is already provided, use it
  if (unitStr && typeof unitStr === "string" && unitStr.trim()) {
    return { amount: cleaned, unit: unitStr.trim() };
  }

  // Try to extract unit from the amount string (e.g., "30g", "125ml")
  const match = amountStr.match(/^([\d\s½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞/.]+)([a-zA-Z]+)/);

  if (match) {
    return { amount: match[1].trim(), unit: match[2] };
  }

  // Check for unit at the end separated by space
  const parts = amountStr.trim().split(/\s+/);
  if (parts.length >= 2) {
    const lastPart = parts[parts.length - 1];
    // If last part is letters only, it might be a unit
    if (/^[a-zA-Z]+$/.test(lastPart)) {
      const amountParts = parts.slice(0, -1).join(" ");
      const cleanedAmount = cleanIngredientAmount(amountParts);
      if (cleanedAmount) {
        return { amount: cleanedAmount, unit: lastPart };
      }
    }
  }

  return { amount: cleaned, unit: "" };
}

/**
 * Validate and sanitize extracted data from AI
 * Returns validated data or throws detailed errors
 */
function validateExtractedData(data: unknown): {
  data: ExtractedRecipe;
  warnings: string[];
} {
  const warnings: string[] = [];

  // Check if data is an object
  if (!data || typeof data !== "object") {
    throw new Error(
      "Invalid response: Expected JSON object, got " + typeof data
    );
  }

  // Type guard: now we know it's an object
  const rawData = data as RawRecipeData;

  // Validate name
  if (!rawData.name || typeof rawData.name !== "string" || !rawData.name.trim()) {
    throw new Error("Recipe name is missing or invalid");
  }

  // Validate serves
  let serves = 4; // Default
  if (rawData.serves !== undefined && rawData.serves !== null) {
    const parsedServes =
      typeof rawData.serves === "number"
        ? rawData.serves
        : parseInt(String(rawData.serves), 10);

    if (isNaN(parsedServes) || parsedServes <= 0) {
      warnings.push(`Invalid serves value "${rawData.serves}", defaulting to 4`);
      serves = 4;
    } else {
      serves = parsedServes;
    }
  }

  // Validate ingredients
  if (!Array.isArray(rawData.ingredients)) {
    throw new Error("Ingredients must be an array");
  }

  if (rawData.ingredients.length === 0) {
    throw new Error("Recipe must have at least one ingredient");
  }

  const validatedIngredients = rawData.ingredients
    .map((ing: unknown, index: number) => {
      // Type guard for ingredient
      if (!ing || typeof ing !== "object") {
        warnings.push(`Ingredient ${index + 1} is invalid, skipping`);
        return null;
      }

      const rawIng = ing as RawIngredient;

      // Validate ingredient name
      if (!rawIng.name || typeof rawIng.name !== "string" || !rawIng.name.trim()) {
        warnings.push(`Ingredient ${index + 1} has no name, skipping`);
        return null;
      }

      return {
        name: String(rawIng.name).trim(),
        amount: rawIng.amount ? String(rawIng.amount) : "",
        unit: rawIng.unit ? String(rawIng.unit) : "",
      };
    })
    .filter((ing): ing is { name: string; amount: string; unit: string } => ing !== null);

  if (validatedIngredients.length === 0) {
    throw new Error("No valid ingredients found");
  }

  // Validate method
  if (!Array.isArray(rawData.method)) {
    throw new Error("Method must be an array");
  }

  if (rawData.method.length === 0) {
    throw new Error("Recipe must have at least one method step");
  }

  const validatedMethod = rawData.method
    .map((step: unknown, index: number) => {
      // Convert to string if not already
      let stepText: string;

      if (typeof step === "string") {
        stepText = step;
      } else if (step && typeof step === "object") {
        const rawStep = step as { step?: string; text?: string };
        stepText = rawStep.step || rawStep.text || String(step);
      } else {
        stepText = String(step);
      }

      if (!stepText || !stepText.trim()) {
        warnings.push(`Method step ${index + 1} is empty, skipping`);
        return null;
      }

      return stepText.trim();
    })
    .filter((step): step is string => step !== null);

  if (validatedMethod.length === 0) {
    throw new Error("No valid method steps found");
  }

  // Validate labels (optional)
  let labels: string[] = [];
  if (rawData.labels !== undefined && rawData.labels !== null) {
    if (!Array.isArray(rawData.labels)) {
      warnings.push("Labels should be an array, ignoring");
    } else {
      labels = rawData.labels
        .filter((label: unknown): label is string => typeof label === "string" && !!label.trim())
        .map((label: string) => label.trim());
    }
  }

  // Validate notes (optional)
  let notes = "";
  if (rawData.notes !== undefined && rawData.notes !== null) {
    if (typeof rawData.notes === "string") {
      notes = rawData.notes.trim();
    } else {
      warnings.push("Notes should be a string, ignoring");
    }
  }

  // Validate calories (optional)
  let calories: number | undefined;
  let caloriesEstimated = true;
  if (rawData.calories !== undefined && rawData.calories !== null) {
    const parsedCalories =
      typeof rawData.calories === "number"
        ? rawData.calories
        : parseInt(String(rawData.calories), 10);

    if (isNaN(parsedCalories) || parsedCalories <= 0) {
      warnings.push(`Invalid calories value "${rawData.calories}", ignoring`);
    } else {
      calories = parsedCalories;
      caloriesEstimated = rawData.caloriesEstimated !== false;
    }
  }

  return {
    data: {
      name: (rawData.name as string).trim(),
      serves,
      ingredients: validatedIngredients,
      method: validatedMethod,
      labels,
      notes,
      calories,
      caloriesEstimated,
    },
    warnings,
  };
}

/**
 * Convert AI-extracted recipe data to full Recipe format with UUIDs
 * Includes comprehensive error handling and data validation
 */
export function convertExtractedToRecipe(extracted: unknown): {
  recipe: Recipe;
  warnings: string[];
} {
  // Validate and sanitize the extracted data
  const { data, warnings } = validateExtractedData(extracted);

  // Process ingredients with error handling
  const ingredients: Ingredient[] = data.ingredients.map((ing, index) => {
    try {
      const { amount, unit } = parseAmountAndUnit(
        ing.amount || "",
        ing.unit || ""
      );

      return {
        id: uuid(),
        name: ing.name,
        amount,
        unit,
      };
    } catch (error: unknown) {
      // Fallback: create ingredient with raw values
      const errorMessage = error instanceof Error ? error.message : String(error);
      warnings.push(
        `Issue parsing ingredient ${index + 1} "${ing.name}": ${errorMessage}`
      );
      return {
        id: uuid(),
        name: ing.name,
        amount: ing.amount || "",
        unit: ing.unit || "",
      };
    }
  });

  // Process method steps with error handling
  const method: MethodStep[] = data.method.map((stepText: string) => ({
    id: uuid(),
    step: stepText,
  }));

  return {
    recipe: {
      name: data.name,
      serves: data.serves,
      ingredients,
      method,
      notes: data.notes,
      labels: data.labels,
      // Omit img/calories fields instead of setting to undefined (Firestore doesn't allow undefined)
      ...(data.calories !== undefined && {
        calories: data.calories,
        caloriesEstimated: data.caloriesEstimated,
      }),
      timesAddedAsMeal: 0,
    },
    warnings,
  };
}

/**
 * Validate that a converted recipe has all required fields
 * More thorough than the initial validation
 */
export function validateExtractedRecipe(recipe: Recipe): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Name validation
  if (!recipe.name?.trim()) {
    errors.push("Recipe name is required");
  } else if (recipe.name.length < 3) {
    warnings.push("Recipe name is very short");
  }

  // Serves validation
  if (!recipe.serves || recipe.serves <= 0) {
    errors.push("Valid serving count is required (must be greater than 0)");
  } else if (recipe.serves > 100) {
    warnings.push("Serving count seems unusually high");
  }

  // Ingredients validation
  if (!recipe.ingredients?.length) {
    errors.push("At least one ingredient is required");
  } else {
    // Check for ingredients without names
    const emptyIngredients = recipe.ingredients.filter(
      (ing) => !ing.name?.trim()
    );
    if (emptyIngredients.length > 0) {
      errors.push(`${emptyIngredients.length} ingredient(s) missing names`);
    }

    // Warn about ingredients without amounts
    const noAmountCount = recipe.ingredients.filter(
      (ing) => !ing.amount || !ing.amount.trim()
    ).length;
    if (noAmountCount > 0) {
      warnings.push(`${noAmountCount} ingredient(s) have no amount specified`);
    }
  }

  // Method validation
  if (!recipe.method?.length) {
    errors.push("At least one method step is required");
  } else {
    // Check for empty method steps
    const emptySteps = recipe.method.filter((step) => !step.step?.trim());
    if (emptySteps.length > 0) {
      errors.push(`${emptySteps.length} method step(s) are empty`);
    }

    // Warn if method steps are very short
    const shortSteps = recipe.method.filter(
      (step) => step.step?.trim().length < 10
    );
    if (shortSteps.length > 0) {
      warnings.push(`${shortSteps.length} method step(s) seem very short`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}
